<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white"> Products </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <table-header-buttons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reload="reloadData"
            @export="exportTable"
            @search="updateSearchPanel"
            :has-selected-lines="selectedProductIds.length > 0"
          />
        </div>
      </v-toolbar>
      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <v-switch
              v-model="available"
              label="Disponible"
              color="success"
              density="compact"
              inset
              hide-details
            />

            <v-switch
              v-model="reserved"
              label="Réservé"
              color="warning"
              density="compact"
              inset
              hide-details
            />

            <v-switch
              v-model="destructed"
              label="Détruit"
              color="error"
              density="compact"
              inset
              hide-details
            />
          </div>
        </v-col>
      </v-row>
      <Table
        :loading="loading"
        :searchField="true"
        :columns="columns"
        :items="products"
        :showSelect="true"
        @edit="edit"
        @rowClicked="rowClicked"
        @selectionChanged="selectionChanged"
      />
    </v-card>

    <v-overlay v-model="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <search-panel
      :rightDrawer="rightDrawer"
      @cancelSearch="cancelSearch"
      @searchData="searchProducts"
    >
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Product" v-model="searchFilter.contain.name" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field
            type="number"
            label="Min Price"
            v-model="searchFilter.greaterThanOrEqual.unitPrice"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field
            type="number"
            label="Max Price"
            v-model="searchFilter.lessThanOrEqual.unitPrice"
          />
        </v-col>
      </v-row>
    </search-panel>

    <ConfirmDialog
      v-model="dialog"
      :dialogTitle="dialogTitle"
      :dialogText="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
      confirm-button-text="Confirmer"
      cancel-button-text="Annuler"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import Table from '@/components/Table.vue'
import TableHeaderButtons from '@/components/TableHeaderButtons.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

import {
  buildSearchFilters,
  buildJsonServerQuery,
  clearSearchFilters
} from '@/utils/app-util'

import { useAppStore } from '@/store/app'
import { createAndSaveDocument } from '@/utils/docx-util'
import { getProductExport, TableColumn, type Product } from '@/types'

import {
  deleteProduct,
  searchForProducts,
  getFilteredProducts,
  getProductById
} from '@/utils/product-api'

import { ReservationStatus } from '@/types'

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false)
const dialogTitle = 'Suppression'
const dialogText = 'Voulez-vous supprimer les produits sélectionnés?'

const loading = ref(false)
const showSearchPanel = ref(false)

const available = ref(true)
const reserved = ref(false)
const destructed = ref(false)

const products = ref<Product[]>([])
const selectedProductIds = ref<string[]>([])

const columns: TableColumn[] = [
  { title: 'N° Aqua', key: 'aquaNb' },
  { title: 'Fabricant', key: 'productMake' },
  { title: 'Modèle', key: 'model' },
  { title: 'N° Série', key: 'serialNb' },
  { title: 'Status', key: 'productStatus' },
  { title: 'Localisation', key: 'location' },
  { title: 'Disponibilité', key: 'reservationStatus' },
  { title: "Date Bon d'entrée", key: 'bonEntreeDate' },
  { title: 'Date de destruction', key: 'destructionDate' },
  { title: '', key: 'actions', sortable: false }
]

const searchFilter = ref({
  contain: { name: '', category: '' },
  greaterThanOrEqual: { unitPrice: 0 },
  lessThanOrEqual: { unitPrice: 0 }
})

let query = ''

const toolTipText = computed(() => 'un produit')

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: v => (showSearchPanel.value = v)
})

watch([available, reserved, destructed], () => {
  saveFiltersToLocalStorage()
  fetchProducts()
})

const selectionChanged = (selected: string[]) =>
  (selectedProductIds.value = [...selected])

const rowClicked = (item: Product) =>
  router.push(`products/${item.id}`)

const edit = (item: Product) =>
  router.push(`products/${item.id}`)

const add = () => router.push('NewProduct')

const remove = () => (dialog.value = true)

const onConfirm = async () => {
  try {
    loading.value = true
    dialog.value = false
    await deleteProduct(selectedProductIds.value)
    await fetchProducts()
    selectedProductIds.value = [];
    appStore.sendSuccessNotice('Product has been deleted.')
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false
  }
}

const onCancel = () => (dialog.value = false)

const searchProducts = async () => {
  buildSearchFilters(searchFilter.value)
  query = buildJsonServerQuery(searchFilter.value)
  products.value = await searchForProducts(query)
}

const cancelSearch = () => (showSearchPanel.value = false)

const reloadData = () => {
  query = ''
  fetchProducts()
}

const exportTable = async () => {
   if (loading.value) {
    return;
  }

  const productIds = [...selectedProductIds.value];
  if (!productIds.length) {
    return;
  }

  try {
    loading.value = true;
    const selectedProducts = await Promise.all(
      productIds.map((id) => getProductById(id))
    );

    createAndSaveDocument(
      'export_products',
      '../templates/ExportProduitsTemplate.docx',
      getProductExport(selectedProducts)
    );
  }catch (e) {
    // Ignore because it's handled in axios service
  }  finally {
    loading.value = false;
  }
}

const updateSearchPanel = () =>
  (showSearchPanel.value = !showSearchPanel.value)

const saveFiltersToLocalStorage = () => {
  localStorage.setItem(
    'productListFilters',
    JSON.stringify({
      available: available.value,
      reserved: reserved.value,
      destructed: destructed.value
    })
  )
}

const loadFiltersFromLocalStorage = () => {
  const saved = localStorage.getItem('productListFilters')
  if (!saved) return
  const f = JSON.parse(saved)
  available.value = f.available ?? true
  reserved.value = f.reserved ?? false
  destructed.value = f.destructed ?? false
}

const fetchProducts = async () => {
  try {
    loading.value = true
    const statuses: ReservationStatus[] = []
    if (available.value) statuses.push(ReservationStatus.AVAILABLE)
    if (reserved.value) statuses.push(ReservationStatus.RESERVED)
    if (destructed.value) statuses.push(ReservationStatus.DETRUIT)
    if (statuses.length === 0) {
      products.value = [];
      return;
    }
    products.value =
      await getFilteredProducts(statuses);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadFiltersFromLocalStorage()
  fetchProducts()
})
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
